import React, { useEffect, useState } from 'react'
import { Box, makeStyles, Button } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSettings, updateSettingById } from 'store/settings'
import CustomTabs from 'components/CustomTabs'
import Wrapper from 'components/Wrapper'
import { Stack } from '@mui/material'
import TemplateCard from 'components/TemplateCard'
import ResourceNotFound from 'components/ResourceNotFound'
import ConfirmationModal from 'components/ConfirmationModal'
import { toast } from 'utils/notify'
import ViewHeader from 'components/ViewHeader'
import { RestoreOutlined as Reset } from '@material-ui/icons'
import { Roles } from 'constants/index'
import { fetchSuperAdminConfiguration } from 'store/config'

const TemplateNames = {
  speakerInvitationEmail: { id: 'speakerInvitationEmail', name: 'Speaker Invitation Email' },
  speakerReminderEmail: { id: 'speakerReminderEmail', name: 'Speaker Reminder Email' },
  // Todo: Hiding this email template - Not needed right now.
  // speakerReminderEmailTalkSubmissions: {
  //   id: 'speakerReminderEmailTalkSubmissions',
  //   name: 'Speaker Reminder Email for Talk Submissions'
  // },
  speakerUpdateInfoEmail: {
    id: 'speakerUpdateInfoEmail',
    name: 'Speaker Data Imported Email'
  },

  orgInvitationEmail: { id: 'orgInvitationEmail', name: 'Organization Invitation Email' },
  orgReminderEmail: { id: 'orgReminderEmail', name: 'Organization Reminder Email' },

  exportLeadsEmail: { id: 'exportLeadsEmail', name: 'Export Leads Email' },
  attendeeInvitationEmail: { id: 'attendeeInvitationEmail', name: 'Attendee Invitation Email' },
  attendeeReminderEmail: { id: 'attendeeReminderEmail', name: 'Attendee Reminder Email' }
}

// People templates - original templates
export const templateTypes = {
  organization: {
    id: 'organization',
    label: 'Organization',
    items: [TemplateNames.orgInvitationEmail, TemplateNames.orgReminderEmail]
  },
  speaker: {
    id: 'speaker',
    label: 'Speaker',
    items: [
      TemplateNames.speakerInvitationEmail,
      TemplateNames.speakerReminderEmail,
      TemplateNames.speakerUpdateInfoEmail
      // TemplateNames.speakerReminderEmailTalkSubmissions
    ]
  },
  attendee: {
    id: 'attendee',
    label: 'Attendee',
    items: [
      TemplateNames.attendeeInvitationEmail,
      TemplateNames.attendeeReminderEmail,
      TemplateNames.exportLeadsEmail
    ]
  }
}

export const SettingForm = () => {
  const { user } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const classes = useStyles()
  const { settings, inProgress } = useSelector((state) => state.setting)
  const [value, setValue] = useState('Organization')
  const [isLoading, setIsLoading] = useState(false)
  const [isResetAllModalOpen, setIsResetAllModalOpen] = useState(false)

  const startLoading = () => setIsLoading(true)
  const stopLoading = () => setIsLoading(false)

  useEffect(() => {
    dispatch(fetchSettings(user?.conferenceId))
  }, [user?.conferenceId])

  const onSaveTemplate = (newValues, template, closeModal) => {
    if (user?.conferenceId) {
      startLoading()
      dispatch(
        updateSettingById(
          user?.conferenceId,
          {
            [template?.id]: {
              subject: newValues?.subject?.trim(),
              body: newValues?.body?.trim()
            }
          },
          () => {
            toast.success('Settings saved.')
            closeModal()
            stopLoading()
          },
          () => {
            toast.error('Could not save template. Try again.')
            stopLoading()
          }
        )
      )
    }
  }

  const handleResetSingleTemplate = async (templateId) => {
    if (user?.conferenceId) {
      try {
        startLoading()
        const configuration = await fetchSuperAdminConfiguration()
        const defaultTemplate = configuration?.emailTemplates?.[templateId]

        if (defaultTemplate) {
          await dispatch(
            updateSettingById(
              user?.conferenceId,
              {
                [templateId]: {
                  subject: defaultTemplate.subject || '',
                  body: defaultTemplate.body || ''
                }
              },
              () => {
                toast.success('Template reset to default.')
                stopLoading()
              },
              () => {
                toast.error('Could not reset template. Try again.')
                stopLoading()
              }
            )
          )
        } else {
          toast.warning('Default template not found.')
          stopLoading()
        }
      } catch (error) {
        toast.error('Could not reset template. Try again.')
        stopLoading()
      }
    }
  }

  const handleResetAllTemplates = async () => {
    if (user?.conferenceId) {
      try {
        startLoading()
        const configuration = await fetchSuperAdminConfiguration()

        if (configuration?.emailTemplates) {
          dispatch(
            updateSettingById(
              user?.conferenceId,
              {
                ...configuration.emailTemplates
              },
              () => {
                toast.success('All templates reset to default.')
                stopLoading()
                setIsResetAllModalOpen(false)
              },
              () => {
                toast.error('Could not reset templates. Try again.')
                stopLoading()
              }
            )
          )
        } else {
          toast.warning('Default templates not found.')
          stopLoading()
        }
      } catch (error) {
        toast.error('Could not reset templates. Try again.')
        stopLoading()
      }
    }
  }

  return (
    <Box>
      <ViewHeader
        title="Email Templates"
        rightComponent={
          user?.role !== Roles.SUPER_ADMIN ? (
            <Button
              startIcon={<Reset />}
              variant="contained"
              color="primary"
              onClick={() => setIsResetAllModalOpen(true)}>
              Reset All Templates
            </Button>
          ) : null
        }
      />
      <Box sx={{ marginTop: '1rem' }}>
        <>
          <CustomTabs
            tabs={Object.values(templateTypes).map((t) => t.label)}
            activeTab={value}
            valueKey="label"
            onChange={(val) => setValue(val)}
          />

          <Wrapper inProgress={inProgress}>
            {value && templateTypes?.[value.toLowerCase()] ? (
              <Stack gap={2} mt={2} className={classes.cards}>
                {templateTypes[value.toLowerCase()].items
                  .map((item) => ({
                    name: item?.name,
                    subject: settings?.[item?.id]?.subject || '',
                    body: settings?.[item?.id]?.body || '',
                    id: item?.id
                  }))
                  .map((item) => (
                    <TemplateCard
                      key={item.id}
                      id={item.id}
                      template={item}
                      isLoading={isLoading}
                      templateId={item.id}
                      onSaveTemplate={(values, closeModal) =>
                        onSaveTemplate(values, item, closeModal)
                      }
                      onResetTemplate={
                        user?.role !== Roles.SUPER_ADMIN ? handleResetSingleTemplate : undefined
                      }
                    />
                  ))}
              </Stack>
            ) : (
              <ResourceNotFound message="No email templates available" />
            )}
          </Wrapper>
        </>
      </Box>

      <ConfirmationModal
        open={isResetAllModalOpen}
        closeModal={() => setIsResetAllModalOpen(false)}
        dialogTitle="Reset All Email Templates"
        dialogText="Are you sure you want to reset ALL email templates to their default values? This action cannot be undone."
        handleConfirm={handleResetAllTemplates}
        inProgress={isLoading}
      />
    </Box>
  )
}

export default SettingForm

const useStyles = makeStyles((theme) => ({
  headBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '1rem',
    zIndex: 99,
    position: 'sticky',
    top: 0,
    background: theme.palette.background.paper
  },
  subText: {
    marginTop: '2px',
    color: '#979797',
    lineHeight: '10px'
  },
  toggleGroup: {
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 500,
      padding: '6px 16px'
    },
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.main
      }
    }
  },
  card: {
    padding: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8px'
  },
  label: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#4A4A4A',
    letterSpacing: '0.5px',
    marginRight: '1rem'
  },
  header: {
    background: '#CAEDF5',
    padding: '12px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    marginBottom: '1rem'
  },
  title: {
    color: 'rgb(66, 66, 66)',
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  },
  headingLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  }
}))
